import { useState, type FormEvent } from 'react'
import { ApiError } from '../../infra/api'
import type { Access } from '../../domain/schemas'
import { Button, Card, ErrorNote, Grid, Inline, Input, Segmented, Stack, Text } from '../../ui'
import { useApi } from '../api-context'
import { AccessChoice } from './People'
import { Field } from './ui'

type Role = 'founder' | 'teammate' | 'viewer'

const ROLES: { value: Role; label: string }[] = [
  { value: 'founder', label: 'Founder' },
  { value: 'teammate', label: 'Teammate' },
  { value: 'viewer', label: 'Viewer' },
]

/**
 * Lists a person. Founders and teammates build, so by default this starts an Add person run (person, Linux user,
 * Paseo, invite, pairing link). Viewers get no box: just the listing. Built from the library; the fields wait on its
 * Field and the box-user switch on its Checkbox (both proposed on /__ui), so they still use today's Field and one
 * classed label.
 */
export function AddPersonForm({ founderId, hasBox, busy, onAdded }: { founderId: string; hasBox: boolean; busy: boolean; onAdded: (runId?: string) => void }) {
  const { api } = useApi()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('teammate')
  const [withRun, setWithRun] = useState(true)
  // Answer 6: teammates share the founder's Linux user unless they get their own.
  const [access, setAccess] = useState<Access>('shared')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string>()

  const runApplies = role !== 'viewer' && hasBox
  const startsRun = runApplies && withRun

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSending(true)
    setError(undefined)
    try {
      const base = { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}), role }
      if (startsRun) {
        const run = await api.createRun({ founderId, template: 'addPerson', options: { ...base, ownUser: access === 'own' } })
        onAdded(run.id)
      } else {
        await api.addPerson({ founderId, ...base })
        setEmail('')
        setName('')
        setOpen(false)
        onAdded()
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setSending(false)
    }
  }

  if (!open) {
    return (
      <Inline>
        <Button size="sm" icon="plus" onClick={() => setOpen(true)}>
          Add a person
        </Button>
      </Inline>
    )
  }
  return (
    <Card>
      <form onSubmit={submit}>
        <Stack gap="sm">
          <Grid cols={2}>
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoCapitalize="off" />
            </Field>
            <Field label="Name (optional)">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </Grid>
          <Segmented label="Role" options={ROLES} value={role} onChange={setRole} />
          {runApplies ? (
            <label className="check">
              <input type="checkbox" checked={withRun} onChange={(e) => setWithRun(e.target.checked)} />
              <span>Set up their access on the box too (starts an Add person run)</span>
            </label>
          ) : (
            <Text size="sm" tone="muted">
              {role === 'viewer' ? 'Viewers only view granted screens; no box user.' : 'No box yet: this lists them. Start an Add person run once the box exists.'}
            </Text>
          )}
          {startsRun && <AccessChoice value={access} onChange={setAccess} admin />}
          {error && <ErrorNote title="They weren't added." reason={error} />}
          <Inline justify="end">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="ink"
              size="sm"
              disabled={sending || !email.includes('@') || (startsRun && busy)}
              title={startsRun && busy ? 'A run is executing for this founder' : undefined}
            >
              {sending ? 'Adding…' : startsRun ? 'Add and start run' : 'Add person'}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Card>
  )
}
